import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import {
  andAllWhere,
  andWhere,
  buildQuery,
  FilterFn,
  noop,
  Ops,
  orderBy,
  OrderVariant,
  orWhere,
  page,
  where,

} from "@albatrosdeveloper/ave-utils-npm/lib/utils/query.util";
import { HttpClient } from "@angular/common/http";
import {
  IPagination,
  IPaginationResult,
  IRequestResult,
} from "../../../../common/interfaces";
import { environment as env } from "../../../../../environments/environment.prod";
import { AbstractService } from "../../../../services/abstract.service";
import RouteGroupAttributes from "@albatrosdeveloper/ave-models-package/lib/schemas/routing/route-group/route-group.entity";
import { LogMiddlewareServiceAttributes, LogMiddlewareServiceDetailAttributes } from "@albatrosdeveloper/ave-models-package";
import { LogStatusEnum } from "@albatrosdeveloper/ave-models-package/lib/enums/log-status.enum";
import { DateHelper } from "../../../../utils/date.helper";

const apiUrl = env.API_AVE_INTEGRATOR_URL;
const moduleName = "log_middleware_service";
const moduleNameDetail = "log_middleware_service_detail"

@Injectable({
  providedIn: "root",
})
export class SystemLatencyService extends AbstractService<
  RouteGroupAttributes,
  LogMiddlewareServiceAttributes,
  LogMiddlewareServiceAttributes
> {
  constructor(
    http: HttpClient,
  ) {
    super(apiUrl, moduleName, http);
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   */
  findAllWithCount(
    options: IPagination<LogMiddlewareServiceAttributes>,
    companyId,
    date,
    status
  ): Observable<IPaginationResult<LogMiddlewareServiceAttributes>> {

    const query = buildQuery<LogMiddlewareServiceAttributes>(
      where('_deleted', Ops.eq(false)),
      andWhere("code", Ops.like(options.search ?? "", "i")),
      orWhere("name", Ops.like(options.search ?? "", "i")),
      andAllWhere("dateStart", Ops.btwnDates(date.start, date.end)),
      companyId ? andAllWhere('company.id' as keyof LogMiddlewareServiceAttributes, Ops.eq(companyId)) : noop(),

      status && status.length > 0 ?
        andAllWhere(
          "status",
          Ops.in(...status)
        )
        : noop(),

      orderBy(
        options.sort ?? "dateStart",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string = apiUrl + `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<LogMiddlewareServiceAttributes>>(API_URL);
  }

  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   */
  findAllWithCounts(
    options: IPagination<LogMiddlewareServiceAttributes>,
    companyId?,
    date?
  ): Observable<IPaginationResult<LogMiddlewareServiceAttributes>> {

    const query = buildQuery<LogMiddlewareServiceAttributes>(
      where('_deleted', Ops.eq(false)),
      andWhere("code", Ops.like(options.search ?? "", "i")),
      orWhere("name", Ops.like(options.search ?? "", "i")),
      andAllWhere("dateStart", Ops.btwnDates(date.start, date.end)),
      companyId && companyId.length > 0 ? andAllWhere('company.id' as keyof LogMiddlewareServiceAttributes, Ops.eq(companyId)) : noop(),

      orderBy(
        options.sort ?? "dateStart",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string = apiUrl + `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<LogMiddlewareServiceAttributes>>(API_URL);
  }

  findById(id: string): Observable<IRequestResult<LogMiddlewareServiceAttributes>> {
    const API_URL: string = apiUrl + `api/${moduleName}/byId/${id}`;
    return this.http.get<IRequestResult<LogMiddlewareServiceAttributes>>(API_URL);
  }

  /**
   * Errores de integración del día actual (status = ERROR, `dateStart` de hoy).
   * Alimenta el badge de errores del header y su modal: por defecto trae los
   * 100 más recientes ordenados por `dateStart` descendente; el modal pasa
   * search/sort/página encima de la misma base. El día se calcula en cada
   * llamada para que el polling del badge cruce la medianoche sin recargar.
   *
   * `silent` suprime el overlay global (`x-ave-ctx: skip-loading`): el badge
   * del header consulta de fondo cada 10 minutos y al volver a la pestaña, y
   * no debe bloquear la pantalla. El modal sigue con el overlay por defecto.
   */
  findRecentErrors(
    options: IPagination<LogMiddlewareServiceAttributes> = {},
    columnFilters: { code?: string; type?: string; companyName?: string } = {},
    silent: boolean = false
  ): Observable<IPaginationResult<LogMiddlewareServiceAttributes>> {
    // Las cláusulas de búsqueda solo se agregan cuando hay término: con
    // Ops.like("") la rama OR matchea todo y, al no repetir el guard
    // _deleted, colaba registros soft-deleted al conteo del badge. La rama
    // OR re-aplica _deleted como en findAllDetailWithCount.
    //
    // Dos cláusulas sobre la misma clave dentro de un bloque se pisan (gana
    // la última), así que cuando conviven la búsqueda global y el filtro de
    // columna sobre `code` se combinan en un único regex con lookaheads
    // (verificado contra la API: el patrón llega crudo a Mongo).
    const hasSearch = !!options.search;
    const codeFilter = columnFilters.code;
    const escapeRegex = (v: string) => v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const codeSearchClause = hasSearch
      ? codeFilter
        ? `(?=.*${escapeRegex(options.search!)})(?=.*${escapeRegex(codeFilter)})`
        : options.search!
      : undefined;
    const today = new Date();
    const query = buildQuery<LogMiddlewareServiceAttributes>(
      where('_deleted', Ops.eq(false)),
      codeSearchClause ? andWhere("code", Ops.like(codeSearchClause, "i")) : noop(),
      hasSearch ? orWhere('_deleted', Ops.eq(false)) : noop(),
      hasSearch ? andWhere("name", Ops.like(options.search!, "i")) : noop(),
      hasSearch && codeFilter ? andWhere("code", Ops.like(codeFilter, "i")) : noop(),
      andAllWhere("status", Ops.eq(LogStatusEnum.ERROR)),
      andAllWhere(
        "dateStart",
        Ops.btwnDates(DateHelper.getStartOfDay(today), DateHelper.getEndOfDay(today))
      ),
      !hasSearch && codeFilter ? andAllWhere("code", Ops.like(codeFilter, "i")) : noop(),
      columnFilters.type ? andAllWhere("type", Ops.like(columnFilters.type, "i")) : noop(),
      columnFilters.companyName
        ? andAllWhere('company.name' as keyof LogMiddlewareServiceAttributes, Ops.like(columnFilters.companyName, "i"))
        : noop(),
      orderBy(
        options.sort ?? "dateStart",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string = apiUrl + `api/${moduleName}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<LogMiddlewareServiceAttributes>>(
      API_URL,
      silent ? { headers: { 'x-ave-ctx': 'skip-loading' } } : {}
    );
  }

  updateSS(id: number, data: any) {
    const API_URL = `http://168.197.69.179:2052/api/pm_middleware_ws/put/${id}`
    return this.http.put<any>(API_URL, data)
  }

  sincronize(id) {
    const API_URL = `http://168.197.69.179:2052/api/pm_middleware_ws/put/${id}`
    return this.http.get<any>(API_URL)
  }

  //detail
  /**
   * Pagination
   * @param options Pagination options
   * @returns Paginated data
   */
  findAllDetailWithCount(
    options: IPagination<LogMiddlewareServiceDetailAttributes>,
    id: string
  ): Observable<IPaginationResult<LogMiddlewareServiceDetailAttributes>> {

    const hasSearch = !!options.search;
    
            /**
             * Construye las condiciones base que SIEMPRE deben aplicarse.
             * Incluye: _deleted=false, filtros de distribución, oficina, estado, fechas.
             *
             * @param useOrWhere - Si es true, inicia un nuevo objeto con orWhere (para el OR lógico)
             */
            const buildBaseFilters = (params: { useOrWhere: boolean }): FilterFn<LogMiddlewareServiceDetailAttributes>[] => {
                const deletedFilter: FilterFn<LogMiddlewareServiceDetailAttributes> = params.useOrWhere
                    ? orWhere('_deleted', Ops.eq(false))  // Inicia nuevo objeto en el array
                    : where('_deleted', Ops.eq(false));   // Primer objeto del array

                  const filters: FilterFn<LogMiddlewareServiceDetailAttributes>[] = [deletedFilter];
    
                return filters;
            };

            const searchByAssetNameQueryObject: FilterFn<LogMiddlewareServiceDetailAttributes>[] = [
                        ...buildBaseFilters({ useOrWhere: false }),
                        hasSearch ? andWhere("documentIdentifier", Ops.like(options.search!, "i")) : noop()
                    ];

           const searchByMessageQueryObject: FilterFn<LogMiddlewareServiceDetailAttributes>[] = hasSearch ? [
                ...buildBaseFilters({ useOrWhere: true }),
                andWhere("message", Ops.like(options.search!, "i"))
              ] : [];

    const query = buildQuery<LogMiddlewareServiceDetailAttributes>(
      ...searchByAssetNameQueryObject,
      ...searchByMessageQueryObject,

      orderBy(
        options.sort ?? "createdAt",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string = apiUrl + `api/${moduleNameDetail}/byLog/${id}?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<LogMiddlewareServiceDetailAttributes>>(API_URL);
  }

  findAllDetailWithCount2(
    options: IPagination<LogMiddlewareServiceDetailAttributes>,
    data: any
  ): Observable<IPaginationResult<LogMiddlewareServiceDetailAttributes>> {
    const arrayQueries: FilterFn<any>[] = [];
    
    if (!data.type) {
      arrayQueries.push(where('_deleted', Ops.eq(false)))
    }

    const query = buildQuery<LogMiddlewareServiceDetailAttributes>(
      ...arrayQueries,
      andWhere('documentIdentifier', Ops.eq(data.documentIdentifier)),

      orderBy(
        options.sort ?? "createdAt",
        options.sortDirection == "ASC" ? OrderVariant.Asc : OrderVariant.Desc
      ),
      page(options.currentPage ?? 1, options.limitPerPage ?? 100)
    );

    const API_URL: string = apiUrl + `api/${moduleNameDetail}/withCount?filter=${encodeURIComponent(JSON.stringify(query))}`;
    return this.http.get<IPaginationResult<LogMiddlewareServiceDetailAttributes>>(API_URL);
  }
}
